import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

type TDiffView = 'unified' | 'split';

interface ISplitRow {
  left: interfaces.IPublicDiffLine | null;
  right: interfaces.IPublicDiffLine | null;
}

const statusLabel: Record<interfaces.TPublicDiffFileStatus, string> = {
  added: 'added',
  modified: 'modified',
  deleted: 'deleted',
  renamed: 'renamed',
};

/**
 * Shared diff viewer (unified + split) for foss.global. Renders parsed
 * `IPublicDiffFile[]` produced server-side from a repo's unified diff. Mirrors the
 * Claude Design `diff.jsx` engine (collapsible per-file diffs, +/- proportion bar,
 * unified/split toggle) without the prototype's inline review affordances. Reused by
 * the commit-detail view and, later, compare / merge-request views.
 */
@customElement('dotglobal-diff')
export class DotglobalDiff extends DeesElement {
  @property({ type: Array })
  public accessor files: interfaces.IPublicDiffFile[] = [];

  @property({ type: String })
  public accessor defaultView: TDiffView = 'unified';

  @property({ type: Boolean })
  public accessor truncated = false;

  @property({ type: Boolean })
  public accessor reviewEnabled = false;

  @state()
  private accessor view: TDiffView = 'unified';

  @state()
  private accessor collapsed = new Set<string>();

  private viewInitialized = false;

  public willUpdate(changedProperties: Map<string, unknown>): void {
    if (!this.viewInitialized && changedProperties.has('defaultView')) {
      this.view = this.defaultView;
      this.viewInitialized = true;
    }
  }

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; }

      .difftoolbar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 14px;
        flex-wrap: wrap;
      }
      .diffsummary { display: flex; align-items: center; gap: 10px; font-size: 12.5px; }
      .diffsummary .files { color: var(--text-muted); }
      .diffsummary .files b { color: var(--text-primary); }
      .ad { color: var(--success); }
      .dl-c { color: var(--error); }

      .seg {
        display: inline-flex;
        padding: 2px;
        border: 1px solid var(--border-default);
        border-radius: 6px;
        background: var(--bg-tertiary);
      }
      .seg button {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 26px;
        padding: 0 11px;
        border-radius: 4px;
        color: var(--text-secondary);
        font-size: 12px;
        font-weight: 500;
      }
      .seg button:hover { color: var(--text-primary); }
      .seg button.on {
        color: var(--text-primary);
        background: var(--bg-primary);
        box-shadow: var(--shadow-xs);
      }

      .truncnote {
        margin-bottom: 14px;
        padding: 10px 14px;
        border: 1px solid color-mix(in srgb, var(--warning) 40%, var(--border-default));
        border-radius: 8px;
        background: color-mix(in srgb, var(--warning) 8%, var(--bg-primary));
        color: var(--text-secondary);
        font-size: 12.5px;
      }

      .fdiff {
        border: 1px solid var(--border-default);
        border-radius: 8px;
        /* clip (not hidden) rounds the corners without becoming a scroll
           container, so the sticky file header still pins against the page. */
        overflow: clip;
        background: var(--bg-primary);
      }
      .fdiff + .fdiff { margin-top: 14px; }
      .fdiff-h {
        display: flex;
        align-items: center;
        gap: 8px;
        min-height: 42px;
        padding: 0 12px;
        background: var(--bg-tertiary);
        border-bottom: 1px solid var(--border-default);
        font-size: 12.5px;
        /* pin each file's header under the sticky top nav while its diff scrolls */
        position: sticky;
        top: var(--nav-h, 52px);
        z-index: 3;
      }
      .fdiff.closed .fdiff-h { border-bottom: 0; }
      .fdiff-chev {
        display: inline-flex;
        align-items: center;
        color: var(--text-muted);
      }
      .fdiff-chev dees-icon { transition: transform 0.15s ease; }
      .fdiff.closed .fdiff-chev dees-icon { transform: rotate(-90deg); }
      .fdiff-name {
        font-family: var(--font-mono);
        font-size: 12.5px;
        color: var(--text-primary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .fdiff-old {
        font-family: var(--font-mono);
        font-size: 11.5px;
        color: var(--text-muted);
      }
      .fdiff-status {
        padding: 1px 7px;
        border-radius: 999px;
        font-size: 10.5px;
        font-weight: 600;
        border: 1px solid transparent;
      }
      .fdiff-status.added { color: var(--success); background: color-mix(in srgb, var(--success) 14%, transparent); }
      .fdiff-status.deleted { color: var(--error); background: color-mix(in srgb, var(--error) 14%, transparent); }
      .fdiff-status.modified { color: var(--text-secondary); background: var(--bg-primary); border-color: var(--border-strong); }
      .fdiff-status.renamed { color: var(--accent); background: color-mix(in srgb, var(--accent) 14%, transparent); }
      .fdiff-counts { font-family: var(--font-mono); font-size: 12px; display: inline-flex; gap: 8px; }

      .dbar { display: inline-flex; gap: 2px; }
      .dbar i { width: 8px; height: 8px; border-radius: 2px; display: block; }
      .dbar i.a { background: var(--success); }
      .dbar i.d { background: var(--error); }
      .dbar i.n { background: var(--border-strong); }

      .fdiff-body { overflow-x: auto; }
      table.dt {
        width: 100%;
        border-collapse: collapse;
        font-family: var(--font-mono);
        font-size: 12.5px;
        line-height: 20px;
      }
      .dt td { vertical-align: top; }
      .dt-hunk td {
        padding: 4px 12px;
        color: var(--accent);
        background: color-mix(in srgb, var(--accent) 7%, var(--bg-secondary));
        border-top: 1px solid var(--border-subtle);
        border-bottom: 1px solid var(--border-subtle);
        white-space: pre;
      }
      .dg {
        width: 1%;
        padding: 0 10px;
        text-align: right;
        color: var(--text-muted);
        user-select: none;
        white-space: nowrap;
        border-right: 1px solid var(--border-subtle);
      }
      .linepick {
        min-width: 26px;
        margin: 0 -7px;
        padding: 0 7px;
        border-radius: 3px;
        color: inherit;
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
        text-align: right;
      }
      .linepick:hover, .linepick:focus-visible {
        color: var(--accent);
        background: color-mix(in srgb, var(--accent) 16%, transparent);
        outline: none;
      }
      .dc {
        padding: 0 12px;
        white-space: pre;
        overflow-wrap: normal;
      }
      .dc .sgn { display: inline-block; width: 1ch; color: var(--text-muted); }
      tr.add .dc, td.dc.add { background: color-mix(in srgb, var(--success) 13%, transparent); }
      tr.add .dg, td.dg.add { background: color-mix(in srgb, var(--success) 16%, transparent); }
      tr.del .dc, td.dc.del { background: color-mix(in srgb, var(--error) 12%, transparent); }
      tr.del .dg, td.dg.del { background: color-mix(in srgb, var(--error) 15%, transparent); }
      td.dc.empty, td.dg.empty {
        background: repeating-linear-gradient(135deg, var(--bg-secondary) 0 6px, transparent 6px 12px);
      }
      /* split view: force an even 50/50 split of the two code columns */
      .dt.split { table-layout: fixed; }
      col.cg-gut { width: 52px; }
      .dt.split td.dc { white-space: pre-wrap; overflow-wrap: anywhere; }
      .dt.split td.dg.mid { border-left: 1px solid var(--border-default); }
      tr.add .dc .sgn { color: var(--success); }
      tr.del .dc .sgn { color: var(--error); }
      td.dc.add .sgn { color: var(--success); }
      td.dc.del .sgn { color: var(--error); }

      .fdiff-binary, .fdiff-nochange {
        padding: 16px 14px;
        color: var(--text-muted);
        font-size: 12.5px;
      }
    `,
  ];

  public render(): TemplateResult {
    const files = this.files ?? [];
    if (!files.length) {
      return html`<div class="fdiff"><div class="fdiff-nochange">No file changes are part of the public mirror for this diff.</div></div>`;
    }
    const totalAdd = files.reduce((sum, file) => sum + file.additions, 0);
    const totalDel = files.reduce((sum, file) => sum + file.deletions, 0);
    return html`
      <div class="difftoolbar">
        <div class="diffsummary">
          <span class="files"><b>${files.length}</b> file${files.length === 1 ? '' : 's'} changed</span>
          <span class="ad">+${totalAdd}</span>
          <span class="dl-c">&minus;${totalDel}</span>
        </div>
        <span class="grow"></span>
        <div class="seg">
          <button class=${this.view === 'unified' ? 'on' : ''} @click=${() => { this.view = 'unified'; }}>${this.icon('Code', 13)} Unified</button>
          <button class=${this.view === 'split' ? 'on' : ''} @click=${() => { this.view = 'split'; }}>${this.icon('Columns2', 13)} Split</button>
        </div>
      </div>
      ${this.truncated
        ? html`<div class="truncnote">This diff is very large, so only the first part is shown here. Open it on the source host to view the full change.</div>`
        : ''}
      <div class="stack gap14">
        ${files.map((file) => this.renderFile(file))}
      </div>
    `;
  }

  private renderFile(fileArg: interfaces.IPublicDiffFile): TemplateResult {
    const isOpen = !this.collapsed.has(fileArg.path);
    return html`
      <div class=${`fdiff ${isOpen ? '' : 'closed'}`}>
        <div class="fdiff-h">
          <button class="fdiff-chev" @click=${() => this.toggleFile(fileArg.path)} aria-label="Toggle file">
            ${this.icon('ChevronDown', 14)}
          </button>
          ${this.icon(fileArg.language === 'md' ? 'FileText' : 'FileCode', 14)}
          ${fileArg.status === 'renamed' && fileArg.previousPath
            ? html`<span class="fdiff-old">${fileArg.previousPath}</span>${this.icon('ArrowRight', 12)}`
            : ''}
          <span class="fdiff-name">${fileArg.path}</span>
          <span class=${`fdiff-status ${fileArg.status}`}>${statusLabel[fileArg.status]}</span>
          <span class="grow"></span>
          <span class="fdiff-counts"><span class="ad">+${fileArg.additions}</span><span class="dl-c">&minus;${fileArg.deletions}</span></span>
          ${this.renderBar(fileArg.additions, fileArg.deletions)}
        </div>
        ${isOpen ? this.renderBody(fileArg) : ''}
      </div>
    `;
  }

  private renderBody(fileArg: interfaces.IPublicDiffFile): TemplateResult {
    if (fileArg.isBinary) {
      return html`<div class="fdiff-binary">Binary file not shown.</div>`;
    }
    if (!fileArg.hunks.length) {
      return html`<div class="fdiff-nochange">No textual changes (mode or metadata only).</div>`;
    }
    const isSplit = this.view === 'split';
    return html`
      <div class="fdiff-body">
        <table class=${`dt ${isSplit ? 'split' : ''}`}>
          ${isSplit
            ? html`<colgroup><col class="cg-gut"><col class="cg-code"><col class="cg-gut"><col class="cg-code"></colgroup>`
            : html`<colgroup><col class="cg-gut"><col class="cg-gut"><col class="cg-code"></colgroup>`}
          <tbody>
            ${fileArg.hunks.map((hunk) => isSplit
              ? this.renderSplitHunk(fileArg, hunk)
              : this.renderUnifiedHunk(fileArg, hunk))}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderUnifiedHunk(
    fileArg: interfaces.IPublicDiffFile,
    hunkArg: interfaces.IPublicDiffHunk,
  ): TemplateResult {
    return html`
      <tr class="dt-hunk"><td colspan="3">${hunkArg.header}</td></tr>
      ${hunkArg.lines.map((line) => html`
        <tr class=${`dl ${line.kind === 'context' ? '' : line.kind}`}>
          <td class="dg">${this.renderLineNumber(fileArg, 'base', line.oldNumber)}</td>
          <td class="dg">${this.renderLineNumber(fileArg, 'head', line.newNumber)}</td>
          <td class="dc"><span class="sgn">${this.sign(line.kind)}</span>${line.content}</td>
        </tr>
      `)}
    `;
  }

  private renderSplitHunk(
    fileArg: interfaces.IPublicDiffFile,
    hunkArg: interfaces.IPublicDiffHunk,
  ): TemplateResult {
    const rows = this.toSplitRows(hunkArg.lines);
    return html`
      <tr class="dt-hunk"><td colspan="4">${hunkArg.header}</td></tr>
      ${rows.map((row) => html`
        <tr class="dl">
          <td class=${`dg ${row.left ? row.left.kind : 'empty'}`}>${this.renderLineNumber(fileArg, 'base', row.left?.oldNumber ?? null)}</td>
          <td class=${`dc ${row.left ? row.left.kind : 'empty'}`}>${row.left ? html`<span class="sgn">${this.sign(row.left.kind)}</span>${row.left.content}` : ''}</td>
          <td class=${`dg mid ${row.right ? row.right.kind : 'empty'}`}>${this.renderLineNumber(fileArg, 'head', row.right?.newNumber ?? null)}</td>
          <td class=${`dc ${row.right ? row.right.kind : 'empty'}`}>${row.right ? html`<span class="sgn">${this.sign(row.right.kind)}</span>${row.right.content}` : ''}</td>
        </tr>
      `)}
    `;
  }

  private toSplitRows(linesArg: interfaces.IPublicDiffLine[]): ISplitRow[] {
    const rows: ISplitRow[] = [];
    let index = 0;
    while (index < linesArg.length) {
      const line = linesArg[index];
      if (line.kind === 'context') {
        rows.push({ left: line, right: line });
        index += 1;
        continue;
      }
      const dels: interfaces.IPublicDiffLine[] = [];
      const adds: interfaces.IPublicDiffLine[] = [];
      while (index < linesArg.length && linesArg[index].kind === 'del') {
        dels.push(linesArg[index]);
        index += 1;
      }
      while (index < linesArg.length && linesArg[index].kind === 'add') {
        adds.push(linesArg[index]);
        index += 1;
      }
      if (!dels.length && !adds.length) {
        index += 1;
        continue;
      }
      const pairCount = Math.max(dels.length, adds.length);
      for (let pairIndex = 0; pairIndex < pairCount; pairIndex += 1) {
        rows.push({ left: dels[pairIndex] ?? null, right: adds[pairIndex] ?? null });
      }
    }
    return rows;
  }

  private renderBar(additionsArg: number, deletionsArg: number): TemplateResult {
    const total = additionsArg + deletionsArg || 1;
    const greenBlocks = Math.max(additionsArg > 0 ? 1 : 0, Math.round((additionsArg / total) * 5));
    const redBlocks = Math.min(5 - greenBlocks, Math.max(deletionsArg > 0 ? 1 : 0, Math.round((deletionsArg / total) * 5)));
    const blocks: Array<'a' | 'd' | 'n'> = [];
    for (let index = 0; index < 5; index += 1) {
      if (index < greenBlocks) {
        blocks.push('a');
      } else if (index < greenBlocks + redBlocks) {
        blocks.push('d');
      } else {
        blocks.push('n');
      }
    }
    return html`<span class="dbar">${blocks.map((block) => html`<i class=${block}></i>`)}</span>`;
  }

  private sign(kindArg: interfaces.TPublicDiffLineKind): string {
    if (kindArg === 'add') {
      return '+';
    }
    if (kindArg === 'del') {
      return '−';
    }
    return ' ';
  }

  private renderLineNumber(
    fileArg: interfaces.IPublicDiffFile,
    sideArg: interfaces.TRepositoryPullRequestReviewSide,
    lineArg: number | null,
  ): TemplateResult | number | string {
    if (lineArg === null) {
      return '';
    }
    if (!this.reviewEnabled) {
      return lineArg;
    }
    const path = sideArg === 'head'
      ? fileArg.path
      : fileArg.previousPath || fileArg.path;
    return html`
      <button
        class="linepick"
        title=${`Comment on ${path}:${lineArg} (${sideArg})`}
        aria-label=${`Comment on ${path} ${sideArg} line ${lineArg}`}
        @click=${(eventArg: Event) => {
          eventArg.stopPropagation();
          this.dispatchEvent(new CustomEvent('dotglobal-diff-comment', {
            detail: { path, side: sideArg, line: lineArg },
            bubbles: true,
            composed: true,
          }));
        }}
      >${lineArg}</button>
    `;
  }

  private toggleFile(pathArg: string): void {
    const next = new Set(this.collapsed);
    if (next.has(pathArg)) {
      next.delete(pathArg);
    } else {
      next.add(pathArg);
    }
    this.collapsed = next;
  }

  private icon(nameArg: string, sizeArg = 15): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${sizeArg}></dees-icon>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-diff': DotglobalDiff;
  }
}
